import React, { useEffect, useMemo, useState } from 'react';

const ERROR_IMG_SRC =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==';

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  /**
   * Optional fallback image to use if `src` fails to load.
   * If the fallback also fails, we'll show an inline error SVG.
   */
  fallbackSrc?: string;
};

export function ImageWithFallback(props: Props) {
  const [didError, setDidError] = useState(false);
  const { src, alt, style, className, fallbackSrc, ...rest } = props;
  const normalizedSrc = typeof src === 'string' && src.trim() ? src : undefined;
  const normalizedFallbackSrc =
    typeof fallbackSrc === 'string' && fallbackSrc.trim()
      ? fallbackSrc
      : undefined;

  const [activeSrc, setActiveSrc] = useState<string | undefined>(normalizedSrc);

  // When the incoming src changes, reset state so we try again.
  useEffect(() => {
    setDidError(false);
    setActiveSrc(normalizedSrc);
  }, [normalizedSrc]);

  const handleError = () => {
    // Try fallback image once, then render error svg.
    if (normalizedFallbackSrc && activeSrc !== normalizedFallbackSrc) {
      setActiveSrc(normalizedFallbackSrc);
      return;
    }
    setDidError(true);
  };

  const errorMeta = useMemo(() => {
    const original = normalizedSrc ?? '';
    const fallback = normalizedFallbackSrc ?? '';
    return { original, fallback };
  }, [normalizedSrc, normalizedFallbackSrc]);

  return didError ? (
    <div
      className={`inline-block bg-gray-100 text-center align-middle ${className ?? ''}`}
      style={style}
    >
      <div className="flex items-center justify-center w-full h-full">
        <img
          src={ERROR_IMG_SRC}
          alt="Error loading image"
          {...rest}
          data-original-url={errorMeta.original}
          data-fallback-url={errorMeta.fallback}
        />
      </div>
    </div>
  ) : (
    <img
      src={activeSrc}
      alt={alt}
      className={className}
      style={style}
      {...rest}
      onError={handleError}
    />
  );
}
